// @ts-nocheck
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface FloatingWindow {
  spaceId: string;
  isMinimized: boolean;
  openedAt: number;
}

interface FloatingChatContextValue {
  windows: FloatingWindow[];
  openChat(spaceId: string): void;
  openChatIfNew(spaceId: string): void;
  closeChat(spaceId: string): void;
  toggleMinimize(spaceId: string): void;
}

const MAX_WINDOWS = 3;

function storageKey(userId: string): string {
  return `floating-chat-windows-${userId}`;
}

function loadWindows(key: string): FloatingWindow[] {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return [];
}

function saveWindows(key: string, windows: FloatingWindow[]) {
  try {
    localStorage.setItem(key, JSON.stringify(windows));
  } catch {
    // ignore
  }
}

const LEGACY_KEY = "floating-chat-windows";

/**
 * One-time migration: if data exists under the old unscoped key, copy it to
 * the user-scoped key (if the user-scoped key has no data yet), then delete
 * the old key so it doesn't persist as orphaned data.
 */
function migrateOldKey(userKey: string) {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const existing = localStorage.getItem(userKey);
    if (!existing) {
      localStorage.setItem(userKey, legacy);
    }
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // ignore storage errors
  }
}

const FloatingChatContext = createContext<FloatingChatContextValue | null>(null);

interface FloatingChatProviderProps {
  children: ReactNode;
  userId: string;
}

export function FloatingChatProvider({ children, userId }: FloatingChatProviderProps) {
  const key = storageKey(userId);
  const [windows, setWindows] = useState<FloatingWindow[]>(() => {
    migrateOldKey(key);
    return loadWindows(key);
  });

  // Reset windows when userId changes (same-tab user switch edge case)
  useEffect(() => {
    setWindows(loadWindows(storageKey(userId)));
  }, [userId]);

  useEffect(() => {
    saveWindows(key, windows);
  }, [key, windows]);

  const openChat = useCallback((spaceId: string) => {
    setWindows((prev) => {
      const existing = prev.find((w) => w.spaceId === spaceId);
      if (existing) {
        return prev.map((w) =>
          w.spaceId === spaceId ? { ...w, isMinimized: false } : w
        );
      }
      const next = [...prev, { spaceId, isMinimized: false, openedAt: Date.now() }];
      if (next.length > MAX_WINDOWS) {
        next.sort((a, b) => a.openedAt - b.openedAt);
        return next.slice(next.length - MAX_WINDOWS);
      }
      return next;
    });
  }, []);

  const openChatIfNew = useCallback((spaceId: string) => {
    setWindows((prev) => {
      if (prev.some((w) => w.spaceId === spaceId)) return prev;
      const next = [...prev, { spaceId, isMinimized: false, openedAt: Date.now() }];
      if (next.length > MAX_WINDOWS) {
        next.sort((a, b) => a.openedAt - b.openedAt);
        return next.slice(next.length - MAX_WINDOWS);
      }
      return next;
    });
  }, []);

  const closeChat = useCallback((spaceId: string) => {
    setWindows((prev) => prev.filter((w) => w.spaceId !== spaceId));
  }, []);

  const toggleMinimize = useCallback((spaceId: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.spaceId === spaceId ? { ...w, isMinimized: !w.isMinimized } : w
      )
    );
  }, []);

  return (
    <FloatingChatContext.Provider value={{ windows, openChat, openChatIfNew, closeChat, toggleMinimize }}>
      {children}
    </FloatingChatContext.Provider>
  );
}

export function useFloatingChat(): FloatingChatContextValue {
  const ctx = useContext(FloatingChatContext);
  if (!ctx) throw new Error("useFloatingChat must be used within FloatingChatProvider");
  return ctx;
}
